import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  FileJson,
  FileSpreadsheet,
  Filter,
  Pause,
  Play,
  RefreshCw,
  Search,
  Tag,
  X,
} from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select } from '@/components/ui/select';
import { Dialog } from '@/components/ui/dialog';
import {
  apiClient,
  type Project,
  type RecentTransmissionLog,
  type TransmissionApplication,
  type TransmissionLogLevel,
} from '../lib/api-client';
import { useToast } from '../hooks/useToast';

interface LogEntry {
  id: string;
  timestamp: number;
  service: string;
  category: TransmissionLogLevel;
  severity: 'low' | 'medium' | 'high';
  message: string;
  metadata?: {
    process?: string;
    pid?: number;
    tags?: string[];
    stack_trace?: string;
    [key: string]: unknown;
  };
  batch_id?: string;
  service_name?: string;
  origin: RecentTransmissionLog['origin'];
  received_at: number;
}

interface ServiceOption {
  id: string;
  name: string;
}

const CATEGORIES: LogEntry['category'][] = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];
const TIME_RANGE_SECONDS: Record<string, number> = {
  '1h': 3600,
  '6h': 21600,
  '24h': 86400,
  '7d': 604800,
};

const categoryStyles: Record<LogEntry['category'], { badge: string; rail: string; dot: string }> = {
  trace: {
    badge: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
    rail: 'border-l-slate-400',
    dot: 'bg-slate-400',
  },
  debug: {
    badge: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300',
    rail: 'border-l-sky-500',
    dot: 'bg-sky-500',
  },
  info: {
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    rail: 'border-l-emerald-500',
    dot: 'bg-emerald-500',
  },
  warn: {
    badge: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    rail: 'border-l-amber-500',
    dot: 'bg-amber-500',
  },
  error: {
    badge: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-950/50 dark:text-orange-300',
    rail: 'border-l-orange-500',
    dot: 'bg-orange-500',
  },
  fatal: {
    badge: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300',
    rail: 'border-l-red-500',
    dot: 'bg-red-500',
  },
};

const severityStyles: Record<LogEntry['severity'], string> = {
  low: 'bg-emerald-500',
  medium: 'bg-amber-500',
  high: 'bg-red-500',
};

function logDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

function formatLogClock(timestamp: number): string {
  const date = logDate(timestamp);
  return `${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })}.${String(date.getMilliseconds()).padStart(3, '0')}`;
}

function safeCsvCell(value: unknown): string {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function normalizedTags(attributes: Record<string, unknown> | undefined): string[] | undefined {
  if (!Array.isArray(attributes?.tags)) return undefined;
  const tags = attributes.tags.filter((tag): tag is string => typeof tag === 'string' && tag.length > 0);
  return tags.length > 0 ? tags : undefined;
}

export function toExplorerLog(log: RecentTransmissionLog, index = 0): LogEntry {
  const timestampMs = log.timestamp_ms || log.received_at * 1_000;
  const metadata = { ...(log.attributes || {}) } as NonNullable<LogEntry['metadata']>;
  const tags = normalizedTags(log.attributes);
  if (tags) metadata.tags = tags;

  return {
    id: log.event_id || `${log.origin.batch_id}:${log.origin.runtime_index ?? index}:${timestampMs}`,
    timestamp: timestampMs / 1_000,
    service: log.origin.service,
    service_name: log.origin.service,
    category: log.level,
    severity: log.level === 'error' || log.level === 'fatal'
      ? 'high'
      : log.level === 'warn'
        ? 'medium'
        : 'low',
    message: log.message,
    metadata,
    batch_id: log.origin.batch_id,
    origin: log.origin,
    received_at: log.received_at,
  };
}

export const LogsPage: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { search: urlSearch } = useSearch({ from: '/logs' });
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [applications, setApplications] = useState<TransmissionApplication[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<LogEntry['category'][]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [searchText, setSearchText] = useState(urlSearch || '');
  const [searchQuery, setSearchQuery] = useState(urlSearch || '');
  const [timeRange, setTimeRange] = useState('24h');

  const requestSequence = useRef(0);
  const hasLoaded = useRef(false);

  useEffect(() => {
    void (async () => {
      try {
        const [projectResponse, applicationResponse] = await Promise.all([
          apiClient.getProjects(),
          apiClient.getTransmissionApplications(),
        ]);
        setProjects(projectResponse.projects || []);
        setApplications(applicationResponse.applications || []);
      } catch (error) {
        console.error('Failed to load transmission filters:', error);
      }
    })();
  }, []);

  const visibleApplications = useMemo(
    () => applications.filter((application) => !selectedProjectId || application.project_id === selectedProjectId),
    [applications, selectedProjectId]
  );

  useEffect(() => {
    if (typeof urlSearch === 'string' && urlSearch !== searchQuery) {
      setSearchText(urlSearch);
      setSearchQuery(urlSearch);
    }
  }, [urlSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchText.trim()), 450);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const loadLogs = useCallback(async (background = false) => {
    const requestId = ++requestSequence.current;
    if (hasLoaded.current || background) setRefreshing(true);
    else setLoading(true);
    setLoadError(null);

    try {
      const response = await apiClient.queryRecentLogs({
        project_id: selectedProjectId || undefined,
        application_id: selectedApplicationId || undefined,
        levels: selectedCategories.length > 0 ? selectedCategories : undefined,
        service: selectedServices[0] || undefined,
        search: searchQuery || undefined,
        since_minutes: Math.floor((TIME_RANGE_SECONDS[timeRange] || TIME_RANGE_SECONDS['24h']) / 60),
        limit: 50,
        include_attributes: true,
      });

      if (requestId !== requestSequence.current) return;
      const receivedLogs = (response.logs || []).map(toExplorerLog);
      const nextLogs = selectedTags.length > 0
        ? receivedLogs.filter((log) => selectedTags.every((tag) => log.metadata?.tags?.includes(tag)))
        : receivedLogs;
      setLogs(nextLogs);
      setHasMore(Boolean(response.page?.has_more));
      setLastUpdated(Date.now());
      setServices((current) => {
        const names = new Set(current.map((service) => service.name));
        for (const log of receivedLogs) names.add(log.service);
        return [...names].sort().map((name) => ({ id: name, name }));
      });
      setKnownTags((current) => {
        const next = new Set(current);
        for (const log of nextLogs) {
          for (const tag of log.metadata?.tags || []) next.add(tag);
        }
        return [...next].sort();
      });
      hasLoaded.current = true;
    } catch (error: any) {
      if (requestId !== requestSequence.current) return;
      const message = error.message || t('logs.loadFailed');
      setLoadError(message);
      if (!background) toastRef.current.error(message);
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [searchQuery, selectedApplicationId, selectedCategories, selectedProjectId, selectedServices, selectedTags, t, timeRange]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = window.setInterval(() => void loadLogs(true), 10000);
    return () => window.clearInterval(interval);
  }, [autoRefresh, loadLogs]);

  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextQuery = searchText.trim();
    if (nextQuery === searchQuery) void loadLogs();
    else setSearchQuery(nextQuery);
  };

  const toggleCategory = useCallback((category: LogEntry['category']) => {
    setSelectedCategories((current) => current.includes(category)
      ? current.filter((value) => value !== category)
      : [...current, category]);
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((current) => current.includes(tag)
      ? current.filter((value) => value !== tag)
      : [...current, tag]);
  }, []);

  const clearAllFilters = () => {
    setSelectedServices([]);
    setSelectedCategories([]);
    setSelectedTags([]);
    setSelectedProjectId('');
    setSelectedApplicationId('');
    setSearchText('');
    setSearchQuery('');
    handleTimeRangeChange('24h');
  };

  const copyToClipboard = useCallback((text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    toastRef.current.success(`${label} ${t('logs.copied')}`);
  }, [t]);

  const handleExport = (format: 'json' | 'csv') => {
    if (logs.length === 0) {
      toastRef.current.error(t('logs.noLogsToExport'));
      return;
    }

    try {
      const content = format === 'json'
        ? JSON.stringify(logs, null, 2)
        : [
          ['Timestamp', 'Service', 'Category', 'Severity', 'Message'].map(safeCsvCell).join(','),
          ...logs.map((log) => [
            logDate(log.timestamp).toISOString(),
            log.service_name || log.service,
            log.category,
            log.severity,
            log.message,
          ].map(safeCsvCell).join(',')),
        ].join('\n');
      const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `logs-${Date.now()}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
      toastRef.current.success(t('logs.exported', { count: logs.length, format: format.toUpperCase() }));
    } catch {
      toastRef.current.error(t('logs.exportFailed'));
    }
  };

  const summary = useMemo(() => ({
    errors: logs.filter((log) => log.category === 'error').length,
    fatal: logs.filter((log) => log.category === 'fatal').length,
    warnings: logs.filter((log) => log.category === 'warn').length,
  }), [logs]);

  const activeFilterCount = selectedServices.length
    + selectedCategories.length
    + selectedTags.length
    + (selectedProjectId ? 1 : 0)
    + (selectedApplicationId ? 1 : 0)
    + (searchQuery ? 1 : 0)
    + (timeRange !== '24h' ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;
  const searchPending = searchText.trim() !== searchQuery;

  const openLog = (log: LogEntry) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  return (
    <AppShell>
      <div className="space-y-4">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className={`h-2 w-2 rounded-full ${autoRefresh ? 'animate-pulse bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
              {autoRefresh ? t('logs.liveEvery') : t('logs.investigationMode')}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('logs.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('logs.subtitle')}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh((current) => !current)}
              className="gap-2"
            >
              {autoRefresh ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {autoRefresh ? t('logs.pauseLive') : t('logs.startLive')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void loadLogs()} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('logs.refresh')}
            </Button>
            <div className="flex overflow-hidden rounded-md border bg-background">
              <button
                type="button"
                onClick={() => handleExport('json')}
                className="inline-flex h-9 items-center gap-1.5 border-r px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <FileJson className="h-4 w-4" /> JSON
              </button>
              <button
                type="button"
                onClick={() => handleExport('csv')}
                className="inline-flex h-9 items-center gap-1.5 px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <FileSpreadsheet className="h-4 w-4" /> CSV
              </button>
            </div>
          </div>
        </header>

        <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-3 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200 sm:flex-row sm:items-center sm:justify-between">
          <p>{t('logs.compatibilityNote')}</p>
          <Link to="/transmission-control" className="shrink-0 font-semibold text-blue-700 hover:underline dark:text-blue-300">
            {t('logs.viewTransmissionControl')} →
          </Link>
        </div>

        <Card className="overflow-hidden border-slate-200 shadow-sm dark:border-slate-800">
          <CardContent className="p-0">
            <form onSubmit={handleSearchSubmit} className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-[minmax(16rem,1.6fr)_minmax(10rem,1fr)_minmax(10rem,1fr)] xl:items-end">
              <div>
                <label htmlFor="logs-search" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('logs.searchLabel')}
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="logs-search"
                    type="search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder={t('logs.searchPlaceholder')}
                    className="h-10 pl-9 pr-24 font-mono text-[13px]"
                  />
                  <span className="pointer-events-none absolute right-3 top-2.5 rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {searchPending ? t('logs.waiting') : t('logs.auto')}
                  </span>
                </div>
              </div>

              <Select
                label={t('logs.timeRange')}
                value={timeRange}
                onChange={(event) => handleTimeRangeChange(event.target.value)}
                options={[
                  { value: '1h', label: t('logs.timeRanges.lastHour') },
                  { value: '6h', label: t('logs.timeRanges.last6Hours') },
                  { value: '24h', label: t('logs.timeRanges.last24Hours') },
                  { value: '7d', label: t('logs.timeRanges.last7Days') },
                ]}
              />

              <Select
                label={t('logs.project')}
                value={selectedProjectId}
                onChange={(event) => {
                  const projectId = event.target.value;
                  setSelectedProjectId(projectId);
                  const selected = applications.find((application) => application.application_id === selectedApplicationId);
                  if (selected && projectId && selected.project_id !== projectId) setSelectedApplicationId('');
                }}
                options={[
                  { value: '', label: t('logs.allProjects') },
                  ...projects.map((project) => ({ value: project.id, label: project.name })),
                ]}
              />

              <Select
                label={t('logs.application')}
                value={selectedApplicationId}
                onChange={(event) => setSelectedApplicationId(event.target.value)}
                options={[
                  { value: '', label: t('logs.allApplications') },
                  ...visibleApplications.map((application) => ({ value: application.application_id, label: application.name })),
                ]}
              />

              <Select
                label={t('logs.service')}
                value={selectedServices[0] || ''}
                onChange={(event) => setSelectedServices(event.target.value ? [event.target.value] : [])}
                options={[
                  { value: '', label: t('logs.allServices') },
                  ...services.map((service) => ({ value: service.name, label: service.name })),
                ]}
              />

              <div className="flex min-w-0 gap-2 xl:justify-end">
                <Button type="submit" className="flex-1 gap-2 lg:flex-none">
                  <Search className="h-4 w-4" />
                  {t('logs.searchAction')}
                </Button>
                <Button
                  type="button"
                  variant={showFilters || selectedCategories.length > 0 || selectedTags.length > 0 ? 'secondary' : 'outline'}
                  onClick={() => setShowFilters((current) => !current)}
                  className="gap-2"
                  aria-expanded={showFilters}
                >
                  <Filter className="h-4 w-4" />
                  {t('logs.filters')}
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">{activeFilterCount}</span>
                  )}
                  {showFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </form>

            {showFilters && (
              <div className="border-t bg-slate-50/70 p-4 dark:bg-slate-950/30">
                <div className="grid gap-5 lg:grid-cols-[1fr_minmax(16rem,0.7fr)]">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.level')}</p>
                      {selectedCategories.length > 0 && (
                        <button type="button" onClick={() => setSelectedCategories([])} className="text-xs font-medium text-primary hover:underline">
                          {t('logs.clearLevels')}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map((category) => {
                        const active = selectedCategories.includes(category);
                        return (
                          <button
                            key={category}
                            type="button"
                            aria-pressed={active}
                            onClick={() => toggleCategory(category)}
                            className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                              active ? categoryStyles[category].badge : 'border-border bg-background text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <span className={`h-2 w-2 rounded-full ${categoryStyles[category].dot}`} />
                            {category}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.tags')}</p>
                      {selectedTags.length > 0 && (
                        <button type="button" onClick={() => setSelectedTags([])} className="text-xs font-medium text-primary hover:underline">
                          {t('logs.clearTags')}
                        </button>
                      )}
                    </div>
                    {knownTags.length > 0 ? (
                      <div className="flex max-h-24 flex-wrap gap-2 overflow-y-auto">
                        {knownTags.map((tag) => {
                          const active = selectedTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              aria-pressed={active}
                              onClick={() => toggleTag(tag)}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                active ? 'border-violet-300 bg-violet-100 text-violet-800 dark:border-violet-700 dark:bg-violet-950 dark:text-violet-200' : 'border-border bg-background text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              <Tag className="h-3 w-3" /> {tag}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('logs.noTags')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 border-t px-4 py-2.5">
                <span className="text-xs font-medium text-muted-foreground">{t('logs.activeFilters')}</span>
                {searchQuery && (
                  <Badge variant="secondary" className="gap-1 font-mono text-[11px]">
                    “{searchQuery}”
                    <button type="button" aria-label={t('logs.removeSearch')} onClick={() => { setSearchText(''); setSearchQuery(''); }}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {selectedProjectId && (
                  <Badge variant="secondary" className="gap-1 text-[11px]">
                    {t('logs.project')}: {projects.find((project) => project.id === selectedProjectId)?.name || selectedProjectId}
                    <button type="button" aria-label={`${t('logs.remove')} ${t('logs.project')}`} onClick={() => { setSelectedProjectId(''); setSelectedApplicationId(''); }}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {selectedApplicationId && (
                  <Badge variant="secondary" className="gap-1 text-[11px]">
                    {t('logs.application')}: {applications.find((application) => application.application_id === selectedApplicationId)?.name || selectedApplicationId}
                    <button type="button" aria-label={`${t('logs.remove')} ${t('logs.application')}`} onClick={() => setSelectedApplicationId('')}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {selectedServices.map((service) => (
                  <Badge key={service} variant="secondary" className="gap-1 text-[11px]">
                    {t('logs.service')}: {service}
                    <button type="button" aria-label={`${t('logs.remove')} ${service}`} onClick={() => setSelectedServices([])}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {selectedCategories.map((category) => (
                  <Badge key={category} className={`gap-1 ${categoryStyles[category].badge}`}>
                    {category}
                    <button type="button" aria-label={`${t('logs.remove')} ${category}`} onClick={() => toggleCategory(category)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {selectedTags.map((tag) => (
                  <Badge key={tag} variant="outline" className="gap-1">
                    <Tag className="h-3 w-3" /> {tag}
                    <button type="button" aria-label={`${t('logs.remove')} ${tag}`} onClick={() => toggleTag(tag)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Button type="button" variant="ghost" size="sm" onClick={clearAllFilters} className="ml-auto h-7 text-xs">
                  {t('logs.clearAll')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-slate-200 shadow-sm dark:border-slate-800">
          <div className="flex flex-col gap-3 border-b bg-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.matches')}</p>
                <p className="text-xl font-bold tabular-nums text-foreground">{logs.length}</p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1.5 text-muted-foreground"><span className="h-2 w-2 rounded-full bg-orange-500" />{summary.errors} {t('logs.errors')}</span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground"><span className="h-2 w-2 rounded-full bg-red-500" />{summary.fatal} {t('logs.fatal')}</span>
                <span className="hidden items-center gap-1.5 text-muted-foreground sm:inline-flex"><span className="h-2 w-2 rounded-full bg-amber-500" />{summary.warnings} {t('logs.warnings')}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {hasMore && <span className="rounded-full bg-blue-50 px-2 py-1 font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">{t('logs.moreAvailable')}</span>}
              {lastUpdated && <span>{t('logs.updated')} {new Date(lastUpdated).toLocaleTimeString()}</span>}
              {refreshing && <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary" />}
            </div>
          </div>

          <CardContent className="p-0">
            {loadError && logs.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-16 text-center">
                <div className="mb-4 rounded-full bg-red-50 p-3 text-red-600 dark:bg-red-950/50 dark:text-red-300">
                  <AlertTriangle className="h-6 w-6" />
                </div>
                <h2 className="font-semibold text-foreground">{t('logs.loadFailed')}</h2>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">{loadError}</p>
                <Button className="mt-4 gap-2" onClick={() => void loadLogs()}>
                  <RefreshCw className="h-4 w-4" /> {t('logs.retry')}
                </Button>
              </div>
            ) : loading ? (
              <div aria-label={t('logs.loadingLogs')} className="divide-y">
                {[0, 1, 2, 3, 4, 5].map((row) => (
                  <div key={row} className="grid grid-cols-[9rem_7rem_1fr] gap-5 px-4 py-4">
                    <div className="h-4 animate-pulse rounded bg-muted" />
                    <div className="h-4 animate-pulse rounded bg-muted" />
                    <div className="h-4 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-16 text-center">
                <div className="mb-4 rounded-full bg-slate-100 p-3 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  <Search className="h-6 w-6" />
                </div>
                <h2 className="font-semibold text-foreground">{hasActiveFilters ? t('logs.noMatches') : t('logs.noRecentLogs')}</h2>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  {hasActiveFilters ? t('logs.noMatchesHint') : t('logs.noRecentLogsHint')}
                </p>
                {hasActiveFilters ? (
                  <Button variant="outline" className="mt-4" onClick={clearAllFilters}>{t('logs.clearAll')}</Button>
                ) : (
                  <Button variant="outline" className="mt-4 gap-2" onClick={() => void loadLogs()}>
                    <RefreshCw className="h-4 w-4" /> {t('logs.refresh')}
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader className="bg-slate-50/80 dark:bg-slate-950/40">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[11rem] pl-5">{t('logs.time')}</TableHead>
                    <TableHead className="w-[8.5rem]">{t('logs.level')}</TableHead>
                    <TableHead className="hidden w-[12rem] md:table-cell">{t('logs.service')}</TableHead>
                    <TableHead>{t('logs.message')}</TableHead>
                    <TableHead className="w-[6rem] text-right"><span className="sr-only">{t('logs.actions')}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      tabIndex={0}
                      onClick={() => openLog(log)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') openLog(log);
                      }}
                      className="group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    >
                      <TableCell className={`border-l-4 py-3 pl-4 align-top ${categoryStyles[log.category].rail}`}>
                        <p className="whitespace-nowrap font-mono text-xs font-medium tabular-nums text-foreground">{formatLogClock(log.timestamp)}</p>
                        <p className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">{logDate(log.timestamp).toLocaleDateString()}</p>
                      </TableCell>
                      <TableCell className="py-3 align-top">
                        <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-wide ${categoryStyles[log.category].badge}`}>
                          {log.category}
                        </Badge>
                        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          <span className={`h-1.5 w-1.5 rounded-full ${severityStyles[log.severity]}`} />
                          {log.severity}
                        </div>
                      </TableCell>
                      <TableCell className="hidden py-3 align-top md:table-cell">
                        <p className="truncate text-xs font-semibold text-foreground">{log.service_name || log.service}</p>
                        <p className="mt-1 truncate font-mono text-[10px] text-muted-foreground">{log.origin.application.id}@{log.origin.release.version}</p>
                      </TableCell>
                      <TableCell className="min-w-[18rem] py-3 align-top">
                        <p className="max-w-3xl break-words font-mono text-[13px] leading-5 text-foreground">{log.message}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 md:hidden">
                          <span className="text-[11px] font-semibold text-muted-foreground">{log.service_name || log.service}</span>
                        </div>
                        {log.metadata?.tags && log.metadata.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {log.metadata.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-950 dark:text-violet-300">{tag}</span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-3 text-right align-top">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={t('logs.copyMessage')}
                            title={t('logs.copyMessage')}
                            onClick={(event) => {
                              event.stopPropagation();
                              copyToClipboard(log.message, t('logs.message'));
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={t('logs.viewDetails')}
                            title={t('logs.viewDetails')}
                            onClick={(event) => {
                              event.stopPropagation();
                              openLog(log);
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {selectedLog && (
        <Dialog
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedLog(null);
          }}
          title={
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-base font-semibold">{t('logs.logDetails')}</p>
                <p className="mt-0.5 font-mono text-[11px] font-normal text-muted-foreground">{selectedLog.id}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2), t('logs.fullLog'))}
                className="gap-2"
              >
                <Copy className="h-4 w-4" /> {t('logs.copyFullLog')}
              </Button>
            </div>
          }
        >
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.time')}</p>
                <p className="mt-1 font-mono text-xs text-foreground">{formatLogClock(selectedLog.timestamp)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.service')}</p>
                <p className="mt-1 truncate text-xs font-medium text-foreground">{selectedLog.service_name || selectedLog.service}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.level')}</p>
                <Badge variant="outline" className={`mt-1 text-[10px] uppercase ${categoryStyles[selectedLog.category].badge}`}>{selectedLog.category}</Badge>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.severity')}</p>
                <p className="mt-1 inline-flex items-center gap-1.5 text-xs capitalize text-foreground"><span className={`h-2 w-2 rounded-full ${severityStyles[selectedLog.severity]}`} />{selectedLog.severity}</p>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.originContext')}</p>
              <div className="grid gap-3 rounded-lg border bg-muted/20 p-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.project')}</p>
                  <p className="mt-1 text-xs font-medium text-foreground">{selectedLog.origin.project.name}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">{selectedLog.origin.project.slug}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.application')}</p>
                  <p className="mt-1 text-xs font-medium text-foreground">{selectedLog.origin.application.name}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">{selectedLog.origin.application.id}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.release')}</p>
                  <p className="mt-1 font-mono text-xs font-medium text-foreground">{selectedLog.origin.release.version}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">{selectedLog.origin.release.build_id}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.callsite')}</p>
                  <p className="mt-1 truncate font-mono text-xs font-medium text-foreground">{selectedLog.origin.callsite_id || t('logs.uninstrumented')}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">{selectedLog.origin.instance_hash || selectedLog.origin.batch_id}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.message')}</label>
                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(selectedLog.message, t('logs.message'))} className="h-7 gap-1.5 text-xs">
                  <Copy className="h-3 w-3" /> {t('logs.copy')}
                </Button>
              </div>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100">{selectedLog.message}</pre>
            </div>

            {selectedLog.metadata?.stack_trace && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.stackTrace')}</label>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(String(selectedLog.metadata?.stack_trace), t('logs.stackTrace'))} className="h-7 gap-1.5 text-xs">
                    <Copy className="h-3 w-3" /> {t('logs.copy')}
                  </Button>
                </div>
                <pre className="max-h-72 overflow-auto whitespace-pre rounded-lg bg-slate-950 p-4 font-mono text-[11px] leading-5 text-slate-100">{selectedLog.metadata.stack_trace}</pre>
              </div>
            )}

            {selectedLog.metadata?.tags && selectedLog.metadata.tags.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('logs.tags')}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedLog.metadata.tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        if (!selectedTags.includes(tag)) toggleTag(tag);
                        setShowDetailModal(false);
                        setSelectedLog(null);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300"
                    >
                      <Tag className="h-3 w-3" /> {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedLog.metadata && (
              <details className="rounded-lg border">
                <summary className="cursor-pointer px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
                  {t('logs.metadata')}
                </summary>
                <div className="border-t p-3">
                  <div className="mb-2 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(JSON.stringify(selectedLog.metadata, null, 2), t('logs.metadata'))} className="h-7 gap-1.5 text-xs">
                      <Copy className="h-3 w-3" /> {t('logs.copy')}
                    </Button>
                  </div>
                  <pre className="max-h-52 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 font-mono text-[11px] text-foreground">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                </div>
              </details>
            )}

            <div className="flex justify-end border-t pt-4">
              <Button variant="outline" onClick={() => { setShowDetailModal(false); setSelectedLog(null); }}>{t('logs.close')}</Button>
            </div>
          </div>
        </Dialog>
      )}
    </AppShell>
  );
};
